// src/js/hero-video.js
/**
 * Hero Video Background Handler
 * Manages video playback, reduced motion detection, and accessibility controls
 */
class HeroVideo {
  constructor() {
    this.videos = document.querySelectorAll('[data-hero-video]');
    this.controls = document.querySelectorAll('[data-video-control]');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.videos.length === 0) return;

    this.init();
  }

  init() {
    // Check for reduced motion preference
    this.handleReducedMotion();

    // Listen for changes in reduced motion preference
    this.prefersReducedMotion.addEventListener('change', () => {
      this.handleReducedMotion();
    });

    // Initialize each video
    this.videos.forEach((video) => {
      this.initVideo(video);
    });

    // Initialize play/pause controls
    this.controls.forEach((control) => {
      this.initControl(control);
    });
  }

  initVideo(video) {
    // Handle video load errors
    video.addEventListener('error', () => {
      this.showFallbackImage(video, 'load error');
    });

    // Ensure video is muted for autoplay policies
    video.muted = true;

    // Function to attempt playing the video
    const attemptPlay = () => {
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Auto-play was prevented (e.g., by browser policy)
          console.warn('Video autoplay prevented:', error);
          this.showFallbackImage(video, 'autoplay prevented');
        });
      }
    };

    // Wait for video to be ready before attempting to play
    // Safari and Firefox need the video to be in a ready state
    if (video.readyState >= 3) {
      // HAVE_FUTURE_DATA or HAVE_ENOUGH_DATA - video is ready
      attemptPlay();
    } else {
      // Wait for the video to be ready to play
      video.addEventListener('canplay', attemptPlay, { once: true });
    }
  }

  initControl(control) {
    control.addEventListener('click', (e) => {
      e.preventDefault();
      this.togglePlayPause(control);
    });

    // Keyboard support (Enter and Space)
    control.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.togglePlayPause(control);
      }
    });
  }

  togglePlayPause(control) {
    // Get the video ID from the button's data attribute
    const videoId = control.getAttribute('data-video-id');

    if (videoId) {
      // Find the specific video by ID
      // For hero section: video-{section.id}
      // For collections: video-{section.id}-1 or video-{section.id}-2
      const targetVideos = document.querySelectorAll(`#video-${videoId}`);

      for (const targetVideo of targetVideos) {
        const isPaused = targetVideo.paused;

        if (isPaused) {
          targetVideo.play();
          this.updateControlState(control, 'playing');
        } else {
          targetVideo.pause();
          this.updateControlState(control, 'paused');
        }
      }
    } else {
      // Fallback: if no video ID, control all videos (backward compatibility)
      this.videos.forEach((video) => {
        const isPaused = video.paused;

        if (isPaused) {
          video.play();
          this.updateControlState(control, 'playing');
        } else {
          video.pause();
          this.updateControlState(control, 'paused');
        }
      });
    }
  }

  updateControlState(control, state) {
    const pauseIcon = control.querySelector('.video-control-icon--pause');
    const playIcon = control.querySelector('.video-control-icon--play');

    if (state === 'playing') {
      control.setAttribute('aria-label', 'Video pausieren');
      control.setAttribute('aria-pressed', 'true');
      pauseIcon?.classList.remove('hidden');
      playIcon?.classList.add('hidden');
    } else {
      control.setAttribute('aria-label', 'Video abspielen');
      control.setAttribute('aria-pressed', 'false');
      pauseIcon?.classList.add('hidden');
      playIcon?.classList.remove('hidden');
    }
  }

  handleReducedMotion() {
    if (this.prefersReducedMotion.matches) {
      // User prefers reduced motion - pause videos and show fallback
      this.videos.forEach((video) => {
        video.pause();
        this.showFallbackImage(video, 'reduced motion preference');
      });

      // Update control state
      this.controls.forEach((control) => {
        this.updateControlState(control, 'paused');
      });
    }
  }

  showFallbackImage(video, reason) {
    console.warn('Showing fallback image for video:', reason, video);

    // Hide the video element
    video.style.display = 'none';

    // The poster image (fallback) will be visible through the poster attribute
    // and the static image elements in the Liquid template

    // Optionally hide the control button if video isn't working
    this.controls.forEach((control) => {
      control.style.display = 'none';
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new HeroVideo();
  });
} else {
  // DOM is already loaded
  new HeroVideo();
}

// Re-initialize on Shopify section editor changes
document.addEventListener('shopify:section:load', () => {
  new HeroVideo();
});
